if (!customElements.get('article-toc')) {
  customElements.define(
    'article-toc',
    class ArticleToc extends HTMLElement {
      connectedCallback() {
        // Post body headings plus the headings of the blog explainer sections
        // below the post, in page order.
        const levels = this.dataset.includeH3 === 'true' ? ['h2', 'h3'] : ['h2'];
        const selector = [
          ...levels.map((level) => `.article-template__content ${level}`),
          '.blog-explainer__heading',
        ].join(', ');
        this.headings = [...document.querySelectorAll(selector)].filter((heading) => heading.textContent.trim());
        if (this.headings.length < 2) return;

        const list = this.querySelector('[data-toc-list]');
        const usedIds = new Set();
        this.links = this.headings.map((heading) => {
          if (!heading.id) heading.id = this.uniqueId(heading.textContent, usedIds);
          usedIds.add(heading.id);

          const link = document.createElement('a');
          link.href = `#${heading.id}`;
          link.textContent = heading.textContent.trim();
          return link;
        });
        list.replaceChildren(
          ...this.links.map((link, index) => {
            const item = document.createElement('li');
            item.className = `article-toc__item article-toc__item--${this.headings[index].tagName.toLowerCase()}`;
            item.append(link);
            return item;
          })
        );
        this.hidden = false;

        this.wrapper = this.closest('.article-template__toc');
        if (this.wrapper?.classList.contains('article-template__toc--sidebar')) this.initSidebar();
      }

      disconnectedCallback() {
        this.teardown?.();
      }

      // Left sidebar on wide screens: the wrapper is stretched from the top of
      // the post to the last section the list points at, and the list sticks
      // inside it, so it follows the reader and stops where the content ends.
      initSidebar() {
        const details = this.querySelector('details');
        const openByDefault = details.open;
        const article = this.closest('.article-template');
        const lastHeading = this.headings[this.headings.length - 1];
        const end = lastHeading.closest('.shopify-section') || article;
        const wide = window.matchMedia('(min-width: 1250px)');

        const layout = () => {
          if (wide.matches) {
            const articleTop = article.getBoundingClientRect().top;
            // With the featured image beside the title, that row is page-wide,
            // so the sidebar starts underneath it rather than overlapping it.
            const split = article.querySelector('.article-template__split');
            const offset = split ? split.getBoundingClientRect().bottom - articleTop : 0;
            this.wrapper.style.top = `${offset}px`;
            this.wrapper.style.height = `${Math.max(end.getBoundingClientRect().bottom - articleTop - offset, 0)}px`;
          } else {
            this.wrapper.style.top = '';
            this.wrapper.style.height = '';
          }
          highlight();
        };

        // Only when the screen crosses the breakpoint, never on resize: the
        // reader opening or closing the list resizes the page, and resetting
        // it here would undo their click.
        const setOpen = () => {
          details.open = wide.matches || openByDefault;
          layout();
        };

        let frame;
        const highlight = () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            if (!wide.matches) return;
            // The current section is the last heading that has scrolled past the top third of the screen.
            const line = window.innerHeight * 0.3;
            let active = 0;
            this.headings.forEach((heading, index) => {
              if (heading.getBoundingClientRect().top <= line) active = index;
            });
            this.links.forEach((link, index) => {
              if (index === active) link.setAttribute('aria-current', 'true');
              else link.removeAttribute('aria-current');
            });
          });
        };

        // Watch the whole main area: images loading in any section between the
        // post and the last heading move where the sidebar has to end.
        const resizeObserver = new ResizeObserver(layout);
        resizeObserver.observe(article.closest('main') || article);
        wide.addEventListener('change', setOpen);
        window.addEventListener('scroll', highlight, { passive: true });
        window.addEventListener('load', layout);
        setOpen();

        this.teardown = () => {
          resizeObserver.disconnect();
          wide.removeEventListener('change', setOpen);
          window.removeEventListener('scroll', highlight);
          window.removeEventListener('load', layout);
        };
      }

      uniqueId(text, usedIds) {
        const base =
          text
            .toLowerCase()
            .trim()
            .replace(/[^\p{L}\p{N}]+/gu, '-')
            .replace(/^-+|-+$/g, '') || 'section';
        let id = base;
        for (let i = 2; usedIds.has(id) || document.getElementById(id); i++) id = `${base}-${i}`;
        return id;
      }
    }
  );
}
